// Copy for the post-login platform onboarding tour. Facts mirror the product code
// (subscription/wallet/organization pages, navData, documentationConfig) — keep
// them in sync when those pages change.

export type Point = { icon: string; title: string; text: string };
export type Faq = { q: string; a: string };

// ----------------------------------------------------------------------
// Organization
// ----------------------------------------------------------------------

export const ORG_CONCEPTS: Point[] = [
  {
    icon: 'mdi:office-building-outline',
    title: 'Organization (tenant)',
    text: 'Your company. The owner sets it up, and it contains every sub-tenant, member, role, group and campaign. Most settings apply to everyone you invite.',
  },
  {
    icon: 'mdi:domain',
    title: 'Sub-tenants',
    text: 'Separate workspaces under the organization, such as brands, teams, locations or agency clients. Each has its own members, roles, campaigns and wallet.',
  },
  {
    icon: 'mdi:account-multiple-outline',
    title: 'Members',
    text: 'People you invite. Each signs in with their own login and is added either at the organization level or scoped to a single sub-tenant.',
  },
  {
    icon: 'mdi:shield-account-outline',
    title: 'Roles',
    text: 'A role is a named package of permissions. What a member can see and do is the sum of the roles they hold in their scope.',
  },
];

export const SUB_TENANT_TYPES: Point[] = [
  { icon: 'mdi:domain', title: 'Standard', text: 'An internal team, brand or location that runs its own campaigns.' },
  { icon: 'mdi:handshake-outline', title: 'Agency', text: 'A client you manage. You can turn on agency markup on its campaign costs.' },
  { icon: 'mdi:city-variant-outline', title: 'Enterprise', text: 'A large division with its own set of roles and members.' },
];

export const SCOPE_SWITCH_POINTS: Point[] = [
  {
    icon: 'mdi:swap-horizontal',
    title: 'Switch workspace',
    text: 'The organization owner gets a selector in the header: pick the organization for org-wide data, or a sub-tenant to work inside it.',
  },
  {
    icon: 'mdi:account-eye-outline',
    title: 'View as a member',
    text: 'Inside a sub-tenant, the owner can pick one member to preview exactly the menus and data that person sees.',
  },
  {
    icon: 'mdi:backup-restore',
    title: 'Reset',
    text: '"Reset to owner permissions" returns you to your full access. Your choice is remembered across page refreshes.',
  },
];

export const AGENCY_MARKUP_FLOW: Point[] = [
  {
    icon: 'mdi:domain',
    title: 'Allow it on the sub-tenant',
    text: 'Team & Access → Sub-Tenants → create or edit a sub-tenant and turn on "Allow agency markup".',
  },
  {
    icon: 'mdi:swap-horizontal',
    title: 'Switch into the sub-tenant',
    text: 'Pick that sub-tenant in the header selector so you are working inside it.',
  },
  {
    icon: 'solar:tag-price-linear',
    title: 'Set the markup',
    text: 'Turn on "Agency markup" on the campaign (default 10%, 0–100%).',
  },
  {
    icon: 'solar:eye-linear',
    title: 'Client sees the marked-up cost',
    text: 'Budgets, totals and every cost metric use the marked-up value.',
  },
];

export const AGENCY_MARKUP_WHERE: Point[] = [
  { icon: 'solar:letter-linear', title: 'Direct Mail', text: 'In the Campaign details step of the Direct Mail wizard.' },
  { icon: 'solar:monitor-linear', title: 'Digital Ads', text: 'In the Budget plan of the Digital Ads wizard, next to your budget.' },
];

export const AGENCY_MARKUP_FAQ: Faq[] = [
  {
    q: 'Who can set agency markup?',
    a: 'Only the organization owner. The field appears in your own Organization view, or inside a sub-tenant that has "Allow agency markup" turned on. It is hidden from everyone else, including when you view the platform as one of your members.',
  },
  {
    q: 'Can I turn it off for a client?',
    a: 'Yes. Turn off "Allow agency markup" on that sub-tenant, or switch off "Agency markup" (0%) on an individual campaign.',
  },
];

export const ORG_FAQ: Faq[] = [
  {
    q: 'I only see a chip in the header, not a selector. Why?',
    a: 'Only the organization owner can switch between workspaces. Everyone else sees a read-only chip showing the organization or the sub-tenant they belong to.',
  },
  {
    q: 'Is switching workspace the same as moving a member?',
    a: 'No. Switching only changes what you are viewing. To change which sub-tenant a member belongs to, use Team & Access → Sub-Tenants → Assign account members.',
  },
  {
    q: 'What is Non Profit Status?',
    a: 'If your organization has USPS® nonprofit postage authorization, upload the acknowledgment letter with your acknowledgment number and CRID under Team & Access → Non Profit Status to get reduced postal rates. Verification usually takes 1–3 business days.',
  },
];

// ----------------------------------------------------------------------
// Team & Access
// ----------------------------------------------------------------------

export const TEAM_FLOW: Point[] = [
  { icon: 'mdi:account-plus-outline', title: 'Invite', text: 'Enter an email and optionally pick a sub-tenant to scope the person to.' },
  { icon: 'mdi:shield-account-outline', title: 'Assign roles', text: 'Choose the roles that decide what they can view, create, publish or manage.' },
  { icon: 'mdi:account-group-outline', title: 'Group', text: 'Optionally put members into groups to organize teams.' },
  { icon: 'mdi:clipboard-check-outline', title: 'Approve', text: 'Review requests that need sign-off before they go live.' },
  { icon: 'mdi:history', title: 'Audit', text: 'See who changed what, when and from where.' },
];

export const ACCESS_LEVELS: Array<{ level: string; who: string; can: string }> = [
  { level: 'Organization owner', who: 'Created the organization', can: 'Everything, including billing, ownership transfer and workspace switching.' },
  { level: 'Organization admin', who: 'Trusted operators', can: 'Invite and manage members, roles, groups, sub-tenants and approvals; view audit logs.' },
  { level: 'Account admin', who: 'Sub-tenant leads', can: 'Manage their sub-tenant and view its members, roles, groups and approvals.' },
  { level: 'Account user', who: 'Day-to-day marketers', can: 'Work in modules their roles allow; view members, roles and sub-tenants.' },
  { level: 'Viewer', who: 'Stakeholders', can: 'Read-only access to members and sub-tenants, plus whatever their roles allow.' },
];

export const PERMISSION_EXAMPLES = [
  'campaign.view',
  'campaign.publish',
  'audience.import',
  'journey.publish',
  'report.export',
  'billing.view',
  'subscription.view',
  'member.invite',
];

export const TEAM_FAQ: Faq[] = [
  {
    q: 'What does the invited person receive?',
    a: 'An email invitation with a secure link. They can sign up or log in with an existing account, and then appear under Organization Members. The status moves from Invited to Active.',
  },
  {
    q: 'Organization level or sub-tenant?',
    a: 'Leave Sub-Tenant empty to invite someone at the organization level. Pick a sub-tenant to limit them to that workspace. Only roles configured for that sub-tenant can be assigned.',
  },
  {
    q: 'What is the difference between groups and roles?',
    a: 'Groups are labels that segment people into teams. Roles define what people can do. Use the Permissions tab to see the full matrix of which role allows which action.',
  },
  {
    q: 'When is approval required?',
    a: 'Your organization\'s approval policy can require sign-off before publishing, or for spend at or above a threshold. Pending requests show in Team & Access → Approvals, where an approver can approve or reject them with a reason.',
  },
  {
    q: 'Something is missing from my menu. Why?',
    a: 'Usually your active role or sub-tenant scope does not include that permission. Ask your organization owner or admin to add a role that grants it.',
  },
];

// ----------------------------------------------------------------------
// Subscription
// ----------------------------------------------------------------------

/** Free-trial length promised by the subscribe flow. */
export const TRIAL_DAYS = 14;

export const SUBSCRIBE_FLOW: Point[] = [
  {
    icon: 'solar:crown-linear',
    title: 'Choose a plan',
    text: 'Open Subscription & Usage and click Choose a Plan, or Select Plan in Compare Plans. Pick Monthly or Yearly (20% off).',
  },
  {
    icon: 'solar:card-linear',
    title: 'Add your card',
    text: 'Click Add Card in the plan dialog. Your details go straight to Stripe, and the dialog reopens with your plan still selected.',
  },
  {
    icon: 'solar:check-circle-linear',
    title: 'Confirm',
    text: 'Choose the card to use and confirm with Pay & Subscribe to complete your subscription.',
  },
  {
    icon: 'solar:gift-linear',
    title: `${TRIAL_DAYS}-day free trial`,
    text: 'Your plan card shows the free trial and the date it ends. Billing starts when the trial ends.',
  },
];

export const PAYMENT_METHOD_POINTS: Point[] = [
  {
    icon: 'solar:card-linear',
    title: 'Credit or debit card',
    text: 'Added through Stripe\'s secure form. Boostt.ai never stores your full card number.',
  },
  {
    icon: 'solar:star-linear',
    title: 'Default card',
    text: 'Plan payments use your default card. If you have several, set one as default under Manage Billing → Update Payment Method.',
  },
  {
    icon: 'solar:buildings-2-linear',
    title: 'Bank account (ACH)',
    text: 'Add routing and account numbers, then verify the two micro-deposits (1–2 business days) from Wallet → Verify Account before using it.',
  },
  {
    icon: 'solar:pen-new-square-linear',
    title: 'Change it later',
    text: 'Manage Billing → Update Payment Method lets you add, remove or change the default card or bank account at any time.',
  },
];

export const PLAN_TIERS: Array<{ name: string; text: string }> = [
  { name: 'Launch', text: 'Get started with the core platform.' },
  { name: 'Growth', text: 'More profiles and journeys; add-on modules available.' },
  { name: 'Scale', text: 'Higher limits for multi-team and multi-channel programs.' },
  { name: 'Enterprise', text: 'Custom limits and pricing. Contact us.' },
];

export const PLAN_DIFFERENTIATORS = [
  'Included profiles',
  'Team members',
  'Active journeys',
  'Active models',
  'SignalStack™',
  'BLYN AI Copilot',
  'AI Decisioning',
];

export const ADD_ONS: Point[] = [
  { icon: 'mdi:pulse', title: 'SignalStack™', text: 'Behavioral signals that power journey decisions.' },
  { icon: 'humbleicons:ai', title: 'Blyn', text: 'The AI marketing agent.' },
  { icon: 'solar:cpu-bolt-linear', title: 'AI Decisioning', text: 'Automated next-best-action decisions.' },
  { icon: 'mdi:account-search-outline', title: 'Web Identity', text: 'Resolve anonymous website visitors to real profiles.' },
];

export const BILLING_RULES: Point[] = [
  { icon: 'solar:calendar-linear', title: 'Monthly or yearly', text: 'Yearly billing saves 20% compared with 12 monthly payments.' },
  { icon: 'solar:round-arrow-up-linear', title: 'Upgrades', text: 'Monthly upgrades are charged a prorated amount right away.' },
  { icon: 'solar:round-arrow-down-linear', title: 'Downgrades', text: 'Take effect at the end of your current billing period.' },
  { icon: 'solar:close-circle-linear', title: 'Canceling', text: 'Your plan stays active until the period ends, then does not renew.' },
];

export const SUBSCRIPTION_FAQ: Faq[] = [
  {
    q: 'Why do I need a card for a free trial?',
    a: `Adding a card is what completes your subscription. It is kept on file so your plan continues without interruption when the ${TRIAL_DAYS}-day trial ends.`,
  },
  {
    q: 'When does my trial end, and what happens then?',
    a: `The trial ends ${TRIAL_DAYS} days after you subscribe. The exact date is on your plan card. When it ends, your plan continues and billing starts on your default payment method.`,
  },
  {
    q: 'Can I end my trial early?',
    a: 'Yes. Manage Billing → Cancel Subscription → End trial. You will not be charged, but your access may stop immediately. A trial cannot be restarted afterward.',
  },
  {
    q: 'How is my plan paid?',
    a: 'The plan fee is charged through Stripe to your card or bank account. It is never taken from the wallet. Add-ons and usage are paid from the wallet.',
  },
  {
    q: 'What happens when I go over a limit?',
    a: 'Each metric shows Available, Near Capacity (80%+) or Over Capacity (100%+). Usage above your plan is billed as overage, and the Estimated Bill card projects your total for the period.',
  },
  {
    q: 'What are AI credits?',
    a: 'Your plan includes a monthly AI credit balance for AI features. When it runs out, AI keeps working in overage mode at a per-1K-credit rate charged to your wallet.',
  },
  {
    q: 'Where are my invoices?',
    a: 'Subscription → Manage Billing lets you update the payment method, view billing history, download invoices and cancel.',
  },
  {
    q: 'Do sub-tenants need their own plan?',
    a: 'No. Sub-tenants use the organization\'s plan, and usage limits and features are shared with the organization owner, who manages it.',
  },
];

// ----------------------------------------------------------------------
// Wallet
// ----------------------------------------------------------------------

export const WALLET_FLOW: Point[] = [
  { icon: 'solar:card-linear', title: 'Payment method', text: 'Add a card, or a verified bank account where enabled.' },
  { icon: 'solar:wallet-money-linear', title: 'Recharge', text: 'Add funds manually (minimum $10) or automatically.' },
  { icon: 'solar:wallet-linear', title: 'Available balance', text: 'Your balance minus any amount on hold for in-flight spend.' },
  { icon: 'solar:bill-list-linear', title: 'Spend', text: 'Campaigns, data and add-ons draw from the balance.' },
];

export const WALLET_SPEND_CATEGORIES = [
  'Direct Mail',
  'EDDM',
  'Informed Delivery',
  'Digital Ads',
  'Email',
  'QR Code',
  'Journeys',
  'Predictions',
  'Lookalike',
  'Audience',
  'Data Purchase',
  'Email Verification',
  'Module Add-Ons',
];

export const WALLET_FAQ: Faq[] = [
  {
    q: 'Are there fees on recharges?',
    a: 'Card recharges carry a 3% payment processing fee, shown before you confirm. Bank (ACH) recharges, where enabled, cost 0.8% capped at $5.',
  },
  {
    q: 'How does auto-recharge work?',
    a: 'Set a threshold and a recharge amount (minimum $10). Whenever your balance drops below the threshold, the recharge amount is charged to your default payment method. The threshold cannot be higher than the recharge amount.',
  },
  {
    q: 'What happens if my balance is too low?',
    a: 'Wallet-funded actions, such as buying an add-on or launching a journey, are blocked until you recharge. Journeys check your wallet balance in their pre-launch check.',
  },
  {
    q: 'Where do I see what I spent?',
    a: 'Wallet → History lists every credit, debit and refund with amount, fee, category and status. You can filter by date, type and category, and export to CSV.',
  },
  {
    q: 'Do sub-tenants share the wallet?',
    a: 'No. Each sub-tenant has its own wallet, cards and history. The organization owner can pay into a sub-tenant\'s wallet with the organization\'s card using "Pay on behalf of sub-account".',
  },
];

// ----------------------------------------------------------------------
// Modules
// ----------------------------------------------------------------------

export type FamilyInfo = {
  label: string;
  icon: string;
  color: string;
  tagline: string;
  overview: string;
  capabilities: Point[];
  getStarted: Point[];
  tip: string;
  faq: Faq[];
};

/** Keyed by navData subheader title, upper-cased. */
export const FAMILIES: Record<string, FamilyInfo> = {
  'COMMAND CENTER': {
    label: 'Command Center',
    icon: 'solar:widget-5-linear',
    color: '#1B8187',
    tagline: 'Your marketing operations cockpit',
    overview:
      'A single view across every channel, with AI recommendations, cross-channel dashboards, attribution, guardrails and client reporting. It adapts to your role, from executive to channel lead.',
    capabilities: [
      { icon: 'solar:users-group-rounded-linear', title: 'Role-based views', text: 'Switch between Executive, Marketing Ops, Channel Lead, Client Lead and Provider Ops views, each showing what that role cares about.' },
      { icon: 'solar:magic-stick-3-linear', title: 'AIRA recommendations', text: 'AI-generated recommendations for your channels and campaigns, with the evidence behind each one.' },
      { icon: 'solar:chart-square-linear', title: 'Cross-channel dashboards', text: 'Every channel side by side: spend, reach, response and results, with a detail panel for each channel.' },
      { icon: 'solar:routing-2-linear', title: 'Attribution', text: 'See each channel\'s contribution, explore conversion paths and compare attribution models.' },
      { icon: 'solar:shield-check-linear', title: 'Guardrails', text: 'A review queue for items that need a human decision before they go further.' },
      { icon: 'solar:case-round-linear', title: 'Agency & client reporting', text: 'Portfolio roll-ups across clients and ready-to-share client reports.' },
    ],
    getStarted: [
      { icon: 'solar:user-id-linear', title: 'Pick your role', text: 'Choose the view that matches your job.' },
      { icon: 'solar:magic-stick-3-linear', title: 'Review recommendations', text: 'Start with what AIRA suggests today.' },
      { icon: 'solar:chart-square-linear', title: 'Drill into a channel', text: 'Open a channel to see its full detail.' },
      { icon: 'solar:shield-check-linear', title: 'Clear the review queue', text: 'Resolve anything guardrails flagged.' },
    ],
    tip: 'Command Center has its own guided tour on the Overview page. Use it for a hands-on walkthrough of each panel.',
    faq: [
      { q: 'Why is Command Center marked Beta?', a: 'It is being rolled out gradually. Features and data sources are still expanding, and some channel views use sample data while their integrations are completed.' },
      { q: 'How is this different from Measure → Dashboard?', a: 'The Dashboard is an executive summary of results. Command Center is where you operate: act on recommendations, review guardrails and manage channels and clients.' },
    ],
  },
  BLYN: {
    label: 'Blyn',
    icon: 'humbleicons:ai',
    color: '#58C3BE',
    tagline: 'Your AI marketing agent',
    overview:
      'Describe what you want in plain language and Blyn builds campaigns, analyzes audiences, generates personas and designs multi-channel journeys with you. There are no forms or wizards, and actions it stages wait for your approval.',
    capabilities: [
      { icon: 'solar:chat-round-dots-linear', title: 'Conversational building', text: 'Ask for a campaign, audience analysis, persona or journey. Blyn works through it with you in a streaming chat.' },
      { icon: 'solar:clipboard-check-linear', title: 'Approvals queue', text: 'Anything Blyn wants to launch or spend is staged for your approval first. Nothing goes live without you.' },
      { icon: 'solar:graph-up-linear', title: 'Forecasts & track record', text: 'See what Blyn expects a plan to deliver, and how its past recommendations actually performed.' },
      { icon: 'solar:bell-linear', title: 'Nudges & alerts', text: 'Blyn proactively points out opportunities and problems across your campaigns.' },
      { icon: 'solar:book-bookmark-linear', title: 'Memory & knowledge', text: 'Blyn remembers your brand, preferences and past decisions, and learns from documents you share.' },
      { icon: 'solar:folder-with-files-linear', title: 'Projects & audit log', text: 'Organize work into project workspaces, with a full log of what Blyn did and why.' },
    ],
    getStarted: [
      { icon: 'humbleicons:ai', title: 'Open Blyn', text: 'Click Agent in the sidebar for a preview, then Open Blyn.' },
      { icon: 'solar:chat-round-dots-linear', title: 'Describe a goal', text: 'For example: "Win back lapsed customers near our stores."' },
      { icon: 'solar:document-text-linear', title: 'Review the plan', text: 'Refine the audience, channels and budget in conversation.' },
      { icon: 'solar:clipboard-check-linear', title: 'Approve', text: 'Approve staged actions to launch them.' },
    ],
    tip: 'Be specific about your goal, audience, location and budget. The more context you give, the better Blyn\'s first plan.',
    faq: [
      { q: 'Can Blyn launch or spend without me?', a: 'No. Actions that launch campaigns or spend money wait in the approvals queue until you approve them.' },
      { q: 'Is Blyn included in my plan?', a: 'It depends on your plan. BLYN AI Copilot is included on some tiers and available as an add-on on others. See Subscription & Usage.' },
    ],
  },
  IRIS: {
    label: 'Iris',
    icon: 'octicon:people-24',
    color: '#58C3BE',
    tagline: 'Predict & Enrich · Personalize & Build',
    overview:
      'Where campaigns start. Build, buy or upload audiences, score and expand them with prediction models, see them on a map, then personalize every piece with logic rules and creatives.',
    capabilities: [
      { icon: 'solar:map-point-search-linear', title: 'Buy a list', text: 'Draw a market area on the map, layer demographic and behavioral criteria, get a live count and price, then save.' },
      { icon: 'solar:upload-linear', title: 'Upload your data', text: 'Import your customer CSV with column mapping, header checks and duplicate-address detection.' },
      { icon: 'solar:cpu-bolt-linear', title: 'Prediction models', text: 'Lookalike finds people like your best customers, Lead Scoring ranks who is most likely to respond, and Persona Assignment segments by profile.' },
      { icon: 'solar:map-point-wave-linear', title: 'Places & polygons', text: 'Save store and event locations and custom map boundaries for geo-fenced targeting.' },
      { icon: 'solar:map-linear', title: 'Visualize & enrich', text: 'See any audience on a map. Audiences without coordinates can be geocoded in one step.' },
      { icon: 'hugeicons:ai-image', title: 'Personalize creative', text: 'Logic files map audience columns to variable fields, and creatives are uploaded or designed in Design Studio.' },
    ],
    getStarted: [
      { icon: 'solar:users-group-rounded-linear', title: 'Get an audience', text: 'Buy a list or upload your own.' },
      { icon: 'solar:cpu-bolt-linear', title: 'Predict (optional)', text: 'Score or expand it with a model.' },
      { icon: 'solar:map-linear', title: 'Check the map', text: 'Confirm coverage in Data Visualizer.' },
      { icon: 'solar:code-file-linear', title: 'Add logic', text: 'Map data fields to your creative.' },
      { icon: 'hugeicons:ai-image', title: 'Add creative', text: 'Upload artwork or design it.' },
    ],
    tip: 'Audiences exported from Predictions appear in Saved Audiences automatically, ready to use in any channel.',
    faq: [
      { q: 'What file format should I upload?', a: 'A CSV with a header row. You map its columns during upload, and duplicate addresses are detected for you.' },
      { q: 'How are audiences and models paid for?', a: 'Purchased lists and prediction models show their price in a checkout step before you confirm, and the cost is charged to your wallet.' },
      { q: 'What formats can creatives be?', a: 'Upload PDF, TIFF, PNG or JPG, or design from scratch in Design Studio.' },
    ],
  },
  HALO: {
    label: 'Halo',
    icon: 'solar:clapperboard-linear',
    color: '#1B8187',
    tagline: 'Activate & Orchestrate',
    overview:
      'Launch campaigns on every channel, including direct mail, USPS Informed Delivery, email, digital ads, EDDM and QR codes. Then connect them into journeys and automation workflows that run continuously.',
    capabilities: [
      { icon: 'solar:letter-linear', title: 'Direct Mail', text: 'A 7-step wizard: details, production, audience, variable logic, creatives with QR, optional Informed Delivery, then review.' },
      { icon: 'solar:inbox-linear', title: 'Informed Delivery & EDDM', text: 'Add a clickable preview to the USPS morning email, or mail every door on chosen postal routes without a list.' },
      { icon: 'solar:mailbox-linear', title: 'Email', text: 'A 4-step wizard with audience verification, templates, connected mailboxes and a shared Unibox for replies.' },
      { icon: 'solar:monitor-linear', title: 'Digital Ads', text: 'Goal, targeting, reach, budget and creatives in 6 steps, with a performance dashboard per campaign.' },
      { icon: 'solar:qr-code-linear', title: 'QR Codes', text: 'Trackable codes that log every scan by time, location and device, and can link to mail, email or ads campaigns.' },
      { icon: 'solar:routing-2-linear', title: 'Journeys & Automation', text: 'Orchestrate channels on a visual canvas with conditions, A/B variants and signals, or run trigger-based workflows continuously.' },
    ],
    getStarted: [
      { icon: 'solar:clapperboard-linear', title: 'Pick a channel', text: 'Open Channels and choose one.' },
      { icon: 'solar:users-group-rounded-linear', title: 'Attach an audience', text: 'Use a saved audience from Iris.' },
      { icon: 'hugeicons:ai-image', title: 'Add creative', text: 'Attach artwork, a template or ad creative.' },
      { icon: 'solar:wallet-linear', title: 'Review & pay', text: 'Check the summary. Cost comes from your wallet.' },
      { icon: 'solar:routing-2-linear', title: 'Orchestrate', text: 'Combine channels in a Journey.' },
    ],
    tip: 'Before launch, journeys run a pre-launch check that verifies creatives, audiences and your wallet balance, so top up first.',
    faq: [
      { q: 'What is the difference between Journeys and Automation Workflows?', a: 'Journeys orchestrate a planned multi-channel program on a canvas, with enrollment and per-touchpoint performance. Automation Workflows react to triggers (enroll, send, wait, branch) and run continuously.' },
      { q: 'Why are RCS and Web channels marked Beta?', a: 'They are previews. The views are available to explore while those channels are being connected.' },
      { q: 'How do I send email from my own domain?', a: 'Connect a mailbox under Email → Email Accounts (Google, Microsoft, SMTP/IMAP or SES) and verify its DKIM, SPF, MX and DMARC records.' },
    ],
  },
  SAGE: {
    label: 'Sage',
    icon: 'solar:phone-calling-linear',
    color: '#ECBF2B',
    tagline: 'Voice AI',
    overview:
      'AI voice agents that call and answer on your behalf. Train them with your knowledge base, connect phone numbers, launch outbound or mailer-to-call campaigns, and review every conversation.',
    capabilities: [
      { icon: 'solar:user-speak-linear', title: 'Voice agents', text: 'Define who speaks: identity, personality, knowledge and guardrails. Build call logic with branching workflows.' },
      { icon: 'solar:phone-calling-linear', title: 'Campaigns & outbound', text: 'Connect mailers to AI calls with QR tracking and attribution, or schedule outbound calling with retry logic.' },
      { icon: 'solar:monitor-camera-linear', title: 'Live supervision', text: 'Monitor live, queued and callback sessions across all campaigns in real time.' },
      { icon: 'solar:phone-rounded-linear', title: 'Never miss a caller', text: 'Missed-call recovery, requested callbacks and transcribed voicemails with alerts.' },
      { icon: 'solar:chat-square-like-linear', title: 'Conversations & analytics', text: 'Transcripts, outcomes, booked appointments and qualified leads from every call, plus voice analytics.' },
      { icon: 'solar:shield-check-linear', title: 'Compliance & integrations', text: 'TCPA settings, number registration and webhooks that send voice events to your systems.' },
    ],
    getStarted: [
      { icon: 'solar:book-bookmark-linear', title: 'Knowledge Base', text: 'Upload documents and URLs.' },
      { icon: 'solar:user-speak-linear', title: 'Create an agent', text: 'Set its identity and guardrails.' },
      { icon: 'solar:phone-rounded-linear', title: 'Add a number', text: 'Get an inbound or outbound number.' },
      { icon: 'solar:phone-calling-linear', title: 'Launch a campaign', text: 'Point the agent at an audience.' },
      { icon: 'solar:monitor-camera-linear', title: 'Watch & learn', text: 'Use Supervisor, Conversations and Analytics.' },
    ],
    tip: 'Voice has its own subscription and billing, separate from your platform plan. New accounts start by choosing a vertical and get free trial sessions.',
    faq: [
      { q: 'How is Voice billed?', a: 'Through Voice Subscription (plan, tier and rolled-over minutes) and Voice Billing (every charge, invoices and usage alerts), both in the Voice menu.' },
      { q: 'What happens when trial sessions run out?', a: 'Your agent stops answering until you choose a plan. Anyone who responded recently still gets a call once you resume.' },
    ],
  },
  MEASURE: {
    label: 'Measure',
    icon: 'solar:chart-2-linear',
    color: '#919EAB',
    tagline: 'Analytics & insights',
    overview:
      'The executive dashboard: KPIs, trends, channel breakdown, lead paths, revenue forecast and AI recommendations, all filterable by date range and campaign.',
    capabilities: [
      { icon: 'solar:chart-square-linear', title: 'KPIs & trends', text: 'Headline metrics and how they are moving over time.' },
      { icon: 'solar:pie-chart-2-linear', title: 'Segments & channels', text: 'Break results down by customer segment and by channel.' },
      { icon: 'solar:routing-2-linear', title: 'Lead paths', text: 'A flow diagram of how leads move from first touch to conversion.' },
      { icon: 'solar:graph-up-linear', title: 'Forecast & recommendations', text: 'Projected revenue and AI recommendations on what to do next.' },
    ],
    getStarted: [
      { icon: 'solar:calendar-linear', title: 'Set a date range', text: 'Choose the period to analyze.' },
      { icon: 'solar:filter-linear', title: 'Filter campaigns', text: 'Focus on the campaigns you care about.' },
      { icon: 'solar:magic-stick-3-linear', title: 'Act on insights', text: 'Review recommendations and adjust.' },
    ],
    tip: 'Each channel also has its own analytics on the campaign detail page, such as QR scans, email opens and ad performance.',
    faq: [
      { q: 'Where are campaign-level results?', a: 'Open any campaign from its channel dashboard to see its own analytics tab. The Dashboard rolls results up across campaigns.' },
    ],
  },
};

/** Keyed by nav path. Items without an entry still render with their title only. */
export const ITEM_DESCRIPTIONS: Record<string, string> = {
  '/command-center': 'Role-based cockpit with AI recommendations, cross-channel dashboards, attribution, guardrails and client reports.',
  '/ai-agent': 'Chat with Blyn to build campaigns, analyze audiences, create personas and design journeys, with an approvals queue and memory.',
  '/predictions': 'Build, evaluate and manage prediction models such as Lookalike, Lead Scoring and Persona Assignment.',
  '/cdm': 'Dashboard of all your audiences, with reach, growth and trends.',
  '/cdm/build-audience': 'Buy a mailing list: draw a market area on the map, layer demographic and behavioral criteria, get a live count and save.',
  '/cdm/upload-audience': 'Upload your own customer CSV, with column mapping, header checks and duplicate-address detection.',
  '/cdm/saved-audiences': 'Every purchased, uploaded and prediction-exported audience. View records, duplicate or download CSV.',
  '/cdm/places-of-interest': 'A library of stores, branches and event locations for geo-fenced targeting, added by search or CSV.',
  '/cdm/saved-polygons': 'Draw and reuse custom map boundaries for targeting.',
  '/visualizer': 'Plot audiences and predictions on a map with places of interest and polygon layers. Geocode audiences missing coordinates.',
  '/logic': 'Map audience columns to variable fields in a creative, such as names, offers and images, to personalize each piece.',
  '/creatives': 'Your library of print-ready artwork for Direct Mail and EDDM.',
  '/creatives/upload': 'Upload finished artwork as PDF, TIFF, PNG or JPG.',
  '/creatives/build': 'Design Studio: a drag-and-drop editor for mailers, flyers and email graphics.',
  '/campaigns': 'Physical mail campaigns with a guided wizard: audience, variable logic, creatives with QR, optional Informed Delivery, then review.',
  '/campaigns/informed-delivery': 'Add an image and clickable link to the USPS morning email recipients get before your mail arrives.',
  '/email': 'Create and track email campaigns (opens, clicks, replies, bounces) with a 4-step wizard.',
  '/email/templates': 'Browse, manage and reuse saved email templates.',
  '/email/identities': 'Connect sending mailboxes and verify DKIM, SPF, MX and DMARC records.',
  '/email/inbox': 'Unibox: one two-way inbox across all your mailboxes.',
  '/ads': 'Digital ad campaigns: goal, targeting, reach, budget and creatives, with a performance dashboard per campaign.',
  '/eddm-campaigns': 'Every Door Direct Mail: pick postal routes on a map. No mailing list needed.',
  '/qr-code': 'Trackable QR codes for mail, email and print. Every scan is logged by time, location and device.',
  '/rcs': 'Rich business messaging (preview).',
  '/web-retargeting': 'Re-engage website visitors across the web (preview).',
  '/web-personalization': 'Personalize your website for known visitors (preview).',
  '/journeys': 'Visual canvas to orchestrate channels with conditions, A/B variants and signals. Watch enrollment and performance per touchpoint.',
  '/automation-flows': 'Trigger-based workflows that enroll contacts, send, wait, branch and act across channels. Build once, run continuously.',
  '/mailhouse': 'Internal tooling for the Mailhouse service.',
  '/mailhouse/partners': 'Manage partner organizations and their portal access.',
  '/mailhouse/xmpie': 'Partner XMPie production endpoints and templates.',
  '/voice-analytics': 'Analyze every call and turn audio into business intelligence.',
  '/voice-agents': 'Your AI workforce: identity, personality, knowledge and guardrails for each agent.',
  '/voice-campaigns': 'Connect mailers to AI calls with audience targeting, QR tracking and attribution.',
  '/voice-outbound': 'Schedule and monitor automated outbound calling with retry logic.',
  '/voice-supervisor': 'Live monitoring of active, queued and callback sessions across campaigns.',
  '/voice-contacts': 'Address book for voice campaigns, added one by one or by CSV.',
  '/voice-segments': 'Save multi-field contact segments for reuse across campaigns.',
  '/voice-recovery': 'Turn missed calls forwarded by your carrier into recovery calls, and track results.',
  '/voice-callbacks': 'Requested callbacks from missed calls, capacity overflow and agent handoffs.',
  '/voice-intents': 'Read-only audit trail of every trigger evaluated into a call intent.',
  '/voice-webhooks': 'Send real-time voice events to your own endpoints.',
  '/voice-voicemails': 'Voicemails from inbound callers, with transcription and alerts.',
  '/voice-workflows': 'Multi-step call logic: branching scripts, retries, compliance checks and lead qualification.',
  '/voice-phone-numbers': 'Get and manage inbound and outbound numbers, compliance registration and routing.',
  '/conversations': 'Transcripts, outcomes, booked appointments and qualified leads from every AI call.',
  '/knowledge-base': 'Upload documents and URLs to train your agents on your business.',
  '/voice-settings': 'Account-wide voice defaults, TCPA compliance and financial settings.',
  '/account/voice-subscription': 'Your voice plan, tier, platform fee and rolled-over minutes.',
  '/account/voice-billing': 'Everything you are charged for voice, with invoices and usage alerts.',
  '/dashboard': 'Executive dashboard with KPIs, trends, segmentation, channel breakdown, lead paths and forecasts.',
};

/** The typical end-to-end path through the platform. `family` links a stage to a module step. */
export const WORKFLOW: Array<{ family: string; title: string; text: string; icon: string }> = [
  { family: 'IRIS', title: 'Define your audience', text: 'Buy, upload or draw an audience, then score or expand it with predictions.', icon: 'octicon:people-24' },
  { family: 'IRIS', title: 'Personalize', text: 'Map data to variable fields with logic rules, then upload or design creatives.', icon: 'hugeicons:ai-image' },
  { family: 'HALO', title: 'Activate channels', text: 'Launch direct mail, email, digital ads, EDDM or QR campaigns.', icon: 'solar:clapperboard-linear' },
  { family: 'HALO', title: 'Orchestrate', text: 'Connect channels into journeys and automation workflows.', icon: 'solar:routing-2-linear' },
  { family: 'SAGE', title: 'Follow up by voice', text: 'Let AI agents call responders and book appointments.', icon: 'solar:phone-calling-linear' },
  { family: 'MEASURE', title: 'Measure', text: 'Track results and act on AI recommendations.', icon: 'solar:chart-2-linear' },
];

export const ACCOUNT_SETTINGS: Array<Point & { path: string; permissions: string[] }> = [
  {
    icon: 'solar:palette-linear',
    title: 'Brand Management',
    text: 'Brand Kits hold your colors, fonts, logos, design style and brand voice, and campaigns pick them up automatically.',
    path: '/account/brands',
    permissions: ['settings.view'],
  },
  {
    icon: 'solar:plug-circle-linear',
    title: 'Integrations',
    text: 'Connect CRMs and data sources (Salesforce, HubSpot, SFTP), calendars and messaging tools, and install your website tracking pixel (see the Tracking pixel step).',
    path: '/integrations',
    permissions: ['integration.view', 'integration.edit'],
  },
  {
    icon: 'mdi:pulse',
    title: 'Signal Weights',
    text: 'Tune how much each behavioral signal counts toward a contact\'s Signal Strength Index in journeys.',
    path: '/account/signal-weights',
    permissions: ['settings.view'],
  },
];

// ----------------------------------------------------------------------
// Tracking pixel (Integrations → Tracking Pixel)
// ----------------------------------------------------------------------

export const PIXEL_PATH = '/integrations?tab=tracking-pixel';

export const PIXEL_FLOW: Point[] = [
  { icon: 'mdi:radar', title: 'Generate', text: 'Integrations → Tracking Pixel → Generate Tracking Pixel.' },
  { icon: 'mdi:content-copy', title: 'Copy the embed code', text: 'Use the copy button next to the script.' },
  { icon: 'solar:code-linear', title: 'Add it to your site', text: 'Paste it into the <head> section of your website.' },
  { icon: 'solar:pulse-linear', title: 'Confirm it fires', text: 'Click "Check if pixel is firing" to see recent activity.' },
];

export const PIXEL_USES: Point[] = [
  {
    icon: 'mdi:account-search-outline',
    title: 'Identify anonymous visitors',
    text: 'Use website visitors in Journeys and Automation Workflows, even before they fill in a form.',
  },
  {
    icon: 'solar:chart-square-linear',
    title: 'Site engagement for every campaign',
    text: 'See how people who got your mail, email or ads went on to engage with your website.',
  },
  {
    icon: 'solar:monitor-linear',
    title: 'Digital Ads attribution',
    text: 'Digital Ads campaigns are attributed automatically. No separate script per campaign.',
  },
  {
    icon: 'solar:map-point-search-linear',
    title: 'Enrich identified visitors',
    text: 'Audience Builder → Create New → Enrich shows identified visitors on a map by state, city and ZIP code.',
  },
];

export const PIXEL_FAQ: Faq[] = [
  {
    q: 'Do I need a pixel for each campaign?',
    a: 'No. There is one tracking pixel per account, and it covers every campaign on that account, including Digital Ads.',
  },
  {
    q: 'What about sub-tenants?',
    a: 'Each workspace has its own pixel. Switch into the sub-tenant, then generate its pixel and add it to that client\'s website.',
  },
  {
    q: 'How do I know it is installed correctly?',
    a: 'On the Tracking Pixel tab, click "Check if pixel is firing". It opens recent pixel activity for your account. If nothing shows up, make sure the script is in the <head> of every page and that the site has been visited since.',
  },
];

export type PlatformGuide = { id: string; label: string; icon: string; steps: string[]; note?: string };

/** Where to paste the embed code on common website platforms. */
export const PIXEL_PLATFORM_GUIDES: PlatformGuide[] = [
  {
    id: 'html',
    label: 'Any website',
    icon: 'solar:code-linear',
    steps: [
      'Open the HTML template shared by every page (often header.html, layout or index.html).',
      'Paste the embed code just before the closing </head> tag.',
      'Save and deploy your site.',
    ],
    note: 'Single-page apps (React, Vue, Angular): add it once to the root index.html so it loads on every route.',
  },
  {
    id: 'wordpress',
    label: 'WordPress',
    icon: 'mdi:wordpress',
    steps: [
      'Install a header-code plugin such as WPCode (Insert Headers and Footers).',
      'Open the plugin\'s header settings.',
      'Paste the embed code in the Header box and save.',
    ],
    note: 'Prefer the plugin to editing header.php, so theme updates don\'t remove the pixel.',
  },
  {
    id: 'shopify',
    label: 'Shopify',
    icon: 'mdi:shopify',
    steps: [
      'Go to Online Store → Themes, then open the ⋯ menu on your live theme and choose Edit code.',
      'Open layout/theme.liquid.',
      'Paste the embed code just before </head> and click Save.',
    ],
  },
  {
    id: 'wix',
    label: 'Wix',
    icon: 'mdi:alpha-w-box-outline',
    steps: [
      'Open Settings → Custom Code (under Advanced).',
      'Click Add Custom Code and paste the embed code.',
      'Apply it to All pages, choose Head as the placement, and apply.',
    ],
    note: 'Wix custom code needs a premium plan with a connected domain.',
  },
  {
    id: 'squarespace',
    label: 'Squarespace',
    icon: 'mdi:squarespace',
    steps: [
      'Open your site settings and find Code Injection.',
      'Paste the embed code into the Header field.',
      'Save.',
    ],
    note: 'Code Injection is available on Squarespace Business plans and above.',
  },
  {
    id: 'webflow',
    label: 'Webflow',
    icon: 'mdi:webpack',
    steps: [
      'Open Site settings → Custom code.',
      'Paste the embed code into Head code and save.',
      'Publish the site so the change goes live.',
    ],
  },
  {
    id: 'gtm',
    label: 'Google Tag Manager',
    icon: 'mdi:google',
    steps: [
      'In your container, go to Tags → New → Tag Configuration → Custom HTML.',
      'Paste the embed code.',
      'Set the trigger to All Pages, save, then Submit and Publish the container.',
    ],
  },
];

export const PIXEL_TROUBLESHOOTING: Point[] = [
  { icon: 'solar:document-text-linear', title: 'On every page', text: 'The script must be in the <head> of every page, not just the home page.' },
  { icon: 'solar:upload-linear', title: 'Published', text: 'Many site builders need you to Publish or deploy before changes go live.' },
  { icon: 'solar:shield-cross-linear', title: 'Ad blockers', text: 'Blockers can stop the pixel while you test. Check in a private window with extensions off.' },
  { icon: 'solar:refresh-linear', title: 'Visit, then check', text: 'Load a few pages on your site, then click "Check if pixel is firing".' },
];
